import type { UserManagerSettings } from 'oidc-client-ts';
import { WebStorageStateStore } from 'oidc-client-ts';
import type { KeycloakConfig } from './types';
import { defaultConfig } from './defaults';

/**
 * Configuration builder for Keycloak integration
 */
export class KeycloakConfigBuilder {
  /**
   * Builds OIDC client configuration from Keycloak config
   *
   * @param userConfig - User-provided Keycloak configuration
   * @returns OIDC client settings ready for react-oidc-context
   * @throws Error if required fields are missing or invalid
   */
  static build(userConfig: KeycloakConfig): UserManagerSettings {
    // Validate required fields
    this.validate(userConfig);

    // Merge with defaults
    const config = this.mergeWithDefaults(userConfig);

    // Determine storage type
    const storageType = config.advanced!.storageType || 'session';
    const storage = storageType === 'local'
      ? window.localStorage
      : window.sessionStorage;

    const { useRefreshToken } = config.silentRenew!;

    // Build OIDC settings
    const oidcSettings: UserManagerSettings & { useRefreshToken?: boolean } = {
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri!,
      post_logout_redirect_uri: config.postLogoutRedirectUri!,
      response_type: config.advanced!.responseType!,
      scope: config.scope!,
      automaticSilentRenew: config.silentRenew!.enabled!,
      loadUserInfo: config.advanced!.loadUserInfo!,
      silent_redirect_uri: config.silentRenew!.silentRedirectUri!,

      // Configure storage for user data and tokens
      userStore: new WebStorageStateStore({ store: storage }),

      // Add PKCE support (required for public clients)
      ...(config.advanced!.codeChallengeMethod && {
        response_mode: 'query',
      }),
    };

    if (useRefreshToken !== undefined) {
      oidcSettings.useRefreshToken = useRefreshToken;
    }

    return oidcSettings;
  }

  /**
   * Validates required configuration fields
   *
   * @param config - Configuration to validate
   * @throws Error if validation fails
   */
  private static validate(config: KeycloakConfig): void {
    if (!config.authority) {
      throw new Error('Keycloak authority is required');
    }

    if (!config.clientId) {
      throw new Error('Keycloak clientId is required');
    }

    // Validate URL format
    try {
      new URL(config.authority);
    } catch {
      throw new Error('Keycloak authority must be a valid URL');
    }
  }

  /**
   * Merges user configuration with defaults
   *
   * @param config - User configuration
   * @returns Merged configuration with all defaults applied
   */
  private static mergeWithDefaults(config: KeycloakConfig): Required<KeycloakConfig> {
    return {
      authority: config.authority,
      clientId: config.clientId,
      redirectUri: config.redirectUri ?? defaultConfig.redirectUri!,
      postLogoutRedirectUri:
        config.postLogoutRedirectUri ?? defaultConfig.postLogoutRedirectUri!,
      scope: config.scope ?? defaultConfig.scope!,
      features: {
        ...defaultConfig.features,
        ...config.features,
      },
      silentRenew: {
        ...defaultConfig.silentRenew,
        ...config.silentRenew,
      },
      advanced: {
        ...defaultConfig.advanced,
        ...config.advanced,
      },
      events: {
        ...defaultConfig.events,
        ...config.events,
      },
    } as Required<KeycloakConfig>;
  }
}

/**
 * Helper function to get configuration from environment variables
 *
 * @returns Partial Keycloak configuration from environment
 */
export function getConfigFromEnv(): Partial<KeycloakConfig> {
  // Check if running in browser with Vite
  if (typeof import.meta === 'undefined') {
    return {};
  }

  // Type-safe access to import.meta.env
  const env = (import.meta as any).env;
  if (!env) {
    return {};
  }

  return {
    authority: env.VITE_KEYCLOAK_AUTHORITY as string | undefined,
    clientId: env.VITE_KEYCLOAK_CLIENT_ID as string | undefined,
    features: {
      deviceFlow: env.VITE_ENABLE_DEVICE_FLOW === 'true',
      sessionMonitor: env.VITE_ENABLE_SESSION_MONITOR === 'true',
    },
  };
}
